import { useQuery } from '@tanstack/react-query';

import { api } from '../api/client';
import type { components } from '../api/schema';
import { unwrap } from '../api/unwrap';
import { buildListQuerySerializer } from '../lib/list-query';

// Plan 08-03 UI-04 — system-wide audit log viewer. Reads `GET /audit-events`
// (Phase 8.5-01 + 8.5-04 absolute paging migration). Read-only — any
// authenticated user; backend already enforces.

export type AuditEventDto = components['schemas']['AuditEvent'];

export interface AuditEventsListInput {
  page: number;
  pageSize: number;
  types?: string;
  actorId?: string;
  targetType?: string;
}

export interface AuditEventsListResult {
  items: AuditEventDto[];
  page: number;
  pageSize: number;
  total: number;
}

export const auditKeys = {
  list: (input: AuditEventsListInput) => ['audit', 'list', input] as const,
};

export const useAuditEvents = (input: AuditEventsListInput) =>
  useQuery({
    queryKey: auditKeys.list(input),
    queryFn: async (): Promise<AuditEventsListResult> => {
      const filter: { action?: string; actorId?: string; targetType?: string } = {};
      // Backend filter key for event-type CSV is `action` (Phase 8.5-01
      // schema). The UI-side label stays «type» for operator clarity.
      if (input.types) filter.action = input.types;
      if (input.actorId) filter.actorId = input.actorId;
      if (input.targetType) filter.targetType = input.targetType;
      const result = await api.GET('/api/audit-events', {
        params: {
          query: {
            sort: '-occurredAt',
            page: String(input.page),
            pageSize: String(input.pageSize),
          },
        },
        querySerializer: buildListQuerySerializer(filter),
      });
      return unwrap(result) as AuditEventsListResult;
    },
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
